// frontend/src/components/ui/enhanced-charts.jsx
import React, { useState, useMemo } from 'react';
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip,
    Legend,
    Label,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    LineChart,
    Line,
    AreaChart,
    Area
} from 'recharts';
import { Card } from './card';
import { Button } from './button';
import { Badge } from './badge';
import { cn } from '../../lib/utils';
import { useBranding } from '../../contexts/BrandingContext';
import { getBrandingColors } from '../../utils/brandingUtils';
import {
    ChartBarIcon,
    EyeIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';

// Enhanced color palette following accessibility guidelines - will be generated dynamically with branding
const getChartColors = (branding) => {
    const colors = getBrandingColors(branding);
    
    return {
        primary: colors.primary,      // Primary brand color
        success: colors.secondary,   // Secondary brand color (usually green)
        warning: colors.accent,      // Accent brand color (usually amber/orange)
        danger: '#EF4444',          // Red - overdue/error (keep red for danger)
        secondary: '#8B5CF6',       // Purple - secondary info
        neutral: '#6B7280',         // Gray - inactive/neutral
        info: '#06B6D4',            // Cyan - informational
        accent: colors.accent        // Accent brand color
    };
};

// Semantic color mapping for different data types - will be generated dynamically with branding
const getSemanticColors = (branding) => {
    const chartColors = getChartColors(branding);
    
    return {
        client: {
            active: chartColors.success,
            prospect: chartColors.primary,
            completed: chartColors.secondary,
            inactive: chartColors.neutral
        },
        task: {
            completed: chartColors.success,
            in_progress: chartColors.primary,
            pending: chartColors.warning,
            overdue: chartColors.danger
        },
        priority: {
            high: chartColors.danger,
            medium: chartColors.warning,
            low: chartColors.success
        }
    };
};

// Custom label component for donut charts
const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.05) return null; // Don't show labels for segments smaller than 5%
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Debug logging
    console.log('CustomLabel:', { name, percent, midAngle, x, y });

    return (
        <text 
            x={x} 
            y={y} 
            fill="white" 
            textAnchor={x > cx ? 'start' : 'end'} 
            dominantBaseline="central"
            fontSize="12"
            fontWeight="600"
            style={{
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.3))'
            }}
        >
            {`${(percent * 100).toFixed(1)}%`}
        </text>
    );
};

// Custom tooltip component with better styling
const CustomTooltip = ({ active, payload, label, chartType = 'pie' }) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0];
    const total = payload.reduce((sum, item) => sum + item.value, 0);
    const percentage = ((data.value / total) * 100).toFixed(1);

    return (
        <div className="bg-popover border border-border rounded-lg shadow-lg p-3 min-w-[160px]">
            <div className="flex items-center gap-2 mb-2">
                <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: data.color }}
                />
                <span className="font-semibold text-popover-foreground">{data.name}</span>
            </div>
            <div className="space-y-1">
                <div className="text-lg font-bold text-popover-foreground">
                    {data.value.toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground">
                    {percentage}% of total
                </div>
                {chartType === 'pie' && (
                    <div className="text-xs text-muted-foreground">
                        Click to filter
                    </div>
                )}
            </div>
        </div>
    );
};

// Enhanced Legend component
const CustomLegend = ({ payload, onItemClick, selectedItems = [] }) => {
    return (
        <div className="flex flex-wrap justify-center gap-3 mt-4">
            {payload.map((entry, index) => {
                const isSelected = selectedItems.includes(entry.value);
                return (
                    <button
                        key={index}
                        onClick={() => onItemClick?.(entry.value)}
                        className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200",
                            "hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring",
                            isSelected ? "bg-muted" : "bg-background border border-border"
                        )}
                    >
                        <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: entry.color }}
                        />
                        <span className={cn(
                            "text-sm font-medium",
                            isSelected ? "text-muted-foreground" : "text-foreground"
                        )}>
                            {entry.value}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                            {entry.payload.value}
                        </Badge>
                    </button>
                );
            })}
        </div>
    );
};

// Enhanced Donut Chart Component
export const EnhancedDonutChart = ({
    data,
    title,
    subtitle,
    dataKey = 'value',
    nameKey = 'name',
    colorKey = 'color',
    height = 300,
    showLegend = true,
    showTooltip = true,
    showDataLabels = true,
    onSegmentClick,
    className = '',
    helpText,
    ...props
}) => {
    const { branding } = useBranding();
    const [selectedItems, setSelectedItems] = useState([]);
    const [hoveredSegment, setHoveredSegment] = useState(null);

    // Calculate total for percentage display
    const total = useMemo(() => {
        const calculatedTotal = data.reduce((sum, item) => sum + item[dataKey], 0);
        console.log('EnhancedDonutChart data:', data);
        console.log('EnhancedDonutChart total:', calculatedTotal);
        return calculatedTotal;
    }, [data, dataKey]);

    const handleSegmentClick = (data) => {
        if (onSegmentClick) {
            onSegmentClick(data);
        }
    };

    const handleLegendClick = (itemName) => {
        setSelectedItems(prev => 
            prev.includes(itemName) 
                ? prev.filter(item => item !== itemName)
                : [...prev, itemName]
        );
    };

    const filteredData = selectedItems.length > 0 
        ? data.filter(item => selectedItems.includes(item[nameKey]))
        : data;

    return (
        <Card className={cn("p-6", className)} {...props}>
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-foreground">{title}</h3>
                    {subtitle && (
                        <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {helpText && (
                        <div className="group relative">
                            <InformationCircleIcon className="h-4 w-4 text-muted-foreground cursor-help" />
                            <div className="absolute right-0 top-6 w-64 p-3 bg-popover border border-border text-popover-foreground text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                                {helpText}
                            </div>
                        </div>
                    )}
                    <Button variant="outline" size="sm">
                        <EyeIcon className="h-4 w-4 mr-2" />
                        Details
                    </Button>
                </div>
            </div>

            {/* Chart */}
            <div className="relative">
                <ResponsiveContainer width="100%" height={height}>
                    <PieChart>
                        <Pie
                            data={filteredData}
                            cx="50%"
                            cy="50%"
                            innerRadius={height * 0.25}
                            outerRadius={height * 0.4}
                            paddingAngle={2}
                            dataKey={dataKey}
                            nameKey={nameKey}
                            onMouseEnter={(data) => setHoveredSegment(data)}
                            onMouseLeave={() => setHoveredSegment(null)}
                            onClick={handleSegmentClick}
                            label={showDataLabels ? CustomLabel : false}
                            labelLine={false}
                        >
                            {filteredData.map((entry, index) => {
                                // Determine color with fallback logic
                                let fillColor = entry[colorKey];
                                if (!fillColor) {
                                    // Try semantic colors for client/task status
                                    fillColor = semanticColors.client[entry[nameKey]] || 
                                              semanticColors.task[entry[nameKey]] || 
                                              chartColors.primary;
                                }
                                
                                return (
                                    <Cell 
                                        key={`cell-${index}`} 
                                        fill={fillColor}
                                        stroke="#fff"
                                        strokeWidth={2}
                                        style={{
                                            filter: hoveredSegment?.name === entry[nameKey] ? 'brightness(1.1)' : 'none',
                                            cursor: 'pointer'
                                        }}
                                    />
                                );
                            })}
                        </Pie>
                        
                        {/* Center content */}
                        <text 
                            x="50%" 
                            y="45%" 
                            textAnchor="middle" 
                            fontSize="24"
                            fontWeight="bold"
                            fill="hsl(var(--foreground))"
                        >
                            {total.toLocaleString()}
                        </text>
                        <text 
                            x="50%" 
                            y="55%" 
                            textAnchor="middle" 
                            fontSize="14"
                            fill="hsl(var(--muted-foreground))"
                        >
                            Total
                        </text>

                        {showTooltip && (
                            <Tooltip content={<CustomTooltip chartType="pie" />} />
                        )}
                    </PieChart>
                </ResponsiveContainer>

            </div>

            {/* Enhanced Legend */}
            {showLegend && (
                <CustomLegend 
                    payload={filteredData.map(entry => {
                        // Use same color logic as the chart
                        let fillColor = entry[colorKey];
                        if (!fillColor) {
                            fillColor = semanticColors.client[entry[nameKey]] || 
                                      semanticColors.task[entry[nameKey]] || 
                                      chartColors.primary;
                        }
                        
                        return {
                            value: entry[nameKey],
                            color: fillColor,
                            payload: entry
                        };
                    })}
                    onItemClick={handleLegendClick}
                    selectedItems={selectedItems}
                />
            )}

        </Card>
    );
};

// Enhanced Bar Chart Component
export const EnhancedBarChart = ({
    data,
    title,
    subtitle,
    xAxisKey = 'name',
    yAxisKey = 'value',
    height = 300,
    showGrid = true,
    showTooltip = true,
    color,
    className = '',
    helpText,
    ...props
}) => {
    const { branding } = useBranding();
    const palette = getChartColors(branding);
    const resolvedColor = palette[color] || color || palette.primary;

    return (
        <Card className={cn("p-6", className)} {...props}>
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-foreground">{title}</h3>
                    {subtitle && (
                        <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {helpText && (
                        <div className="group relative">
                            <InformationCircleIcon className="h-4 w-4 text-muted-foreground cursor-help" />
                            <div className="absolute right-0 top-6 w-64 p-3 bg-popover border border-border text-popover-foreground text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                                {helpText}
                            </div>
                        </div>
                    )}
                    <Button variant="outline" size="sm">
                        <ChartBarIcon className="h-4 w-4 mr-2" />
                        Export
                    </Button>
                </div>
            </div>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={height}>
                <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />}
                    <XAxis 
                        dataKey={xAxisKey} 
                        tick={{ fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                    />
                    <YAxis 
                        tick={{ fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                    />
                    {showTooltip && (
                        <Tooltip content={<CustomTooltip chartType="bar" />} />
                    )}
                    <Bar 
                        dataKey={yAxisKey} 
                        fill={resolvedColor}
                        radius={[4, 4, 0, 0]}
                    />
                </BarChart>
            </ResponsiveContainer>
        </Card>
    );
};

// Enhanced Line Chart Component
export const EnhancedLineChart = ({
    data,
    title,
    subtitle,
    xAxisKey = 'name',
    yAxisKey = 'value',
    height = 300,
    showGrid = true,
    showTooltip = true,
    color,
    className = '',
    helpText,
    ...props
}) => {
    const { branding } = useBranding();
    const palette = getChartColors(branding);
    const resolvedColor = palette[color] || color || palette.primary;

    return (
        <Card className={cn("p-6", className)} {...props}>
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-foreground">{title}</h3>
                    {subtitle && (
                        <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {helpText && (
                        <div className="group relative">
                            <InformationCircleIcon className="h-4 w-4 text-muted-foreground cursor-help" />
                            <div className="absolute right-0 top-6 w-64 p-3 bg-popover border border-border text-popover-foreground text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                                {helpText}
                            </div>
                        </div>
                    )}
                    <Button variant="outline" size="sm">
                        <ArrowTrendingUpIcon className="h-4 w-4 mr-2" />
                        Trend
                    </Button>
                </div>
            </div>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={height}>
                <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />}
                    <XAxis 
                        dataKey={xAxisKey} 
                        tick={{ fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                    />
                    <YAxis 
                        tick={{ fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                    />
                    {showTooltip && (
                        <Tooltip content={<CustomTooltip chartType="line" />} />
                    )}
                    <Line 
                        type="monotone" 
                        dataKey={yAxisKey} 
                        stroke={resolvedColor}
                        strokeWidth={3}
                        dot={{ fill: resolvedColor, strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, stroke: resolvedColor, strokeWidth: 2 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </Card>
    );
};

// Chart Loading State
export const ChartLoadingState = ({ height = 300 }) => (
    <Card className="p-6">
        <div className="animate-pulse">
            <div className="h-6 bg-muted rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-muted rounded w-1/2 mb-6"></div>
            <div 
                className="bg-muted rounded flex items-center justify-center"
                style={{ height }}
            >
            </div>
        </div>
    </Card>
);

// Chart Error State
export const ChartErrorState = ({ error, onRetry, height = 300 }) => (
    <Card className="p-6">
        <div 
            className="flex flex-col items-center justify-center text-center"
            style={{ height }}
        >
            <div className="text-red-500 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">Chart Error</h3>
            <p className="text-muted-foreground mb-4">{error || 'Failed to load chart data'}</p>
            {onRetry && (
                <Button onClick={onRetry} variant="outline">
                    Try Again
                </Button>
            )}
        </div>
    </Card>
);

export { 
    getChartColors,
    getSemanticColors
};
